import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  Users,
  Car,
  RefreshCw,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Flame,
} from "lucide-react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";

// HeatLayer component that uses leaflet.heat
const HeatLayer = ({ points, options }) => {
  const map = useMap();
  const heatLayerRef = useRef(null);

  useEffect(() => {
    if (!points || points.length === 0) {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
      return;
    }

    // Convert points to format expected by leaflet.heat: [lat, lng, intensity]
    const heatData = points.map(p => [p.lat, p.lng, p.weight || 1]);

    if (heatLayerRef.current) {
      heatLayerRef.current.setLatLngs(heatData);
    } else {
      heatLayerRef.current = L.heatLayer(heatData, {
        radius: options?.radius || 25,
        blur: options?.blur || 15,
        maxZoom: options?.maxZoom || 17,
        max: options?.max || 2.0,
        gradient: options?.gradient || {
          0.0: '#3b82f6',
          0.25: '#22c55e',
          0.5: '#eab308',
          0.75: '#f97316',
          1.0: '#ef4444'
        }
      }).addTo(map);
    }

    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
    };
  }, [points, options, map]);

  return null;
};

// Map center updater component
const MapCenterUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], zoom || 10);
    }
  }, [center, zoom, map]);
  return null;
};

export const RideHeatMap = ({ isCityAdmin = false, cityId = null }) => {
  const { user } = useAuth();
  const [heatmapData, setHeatmapData] = useState({ points: [], center: { lat: 20.5937, lng: 78.9629 } });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heatmapType, setHeatmapType] = useState("demand");
  const [timeRange, setTimeRange] = useState("7");
  const [selectedCity, setSelectedCity] = useState(cityId || "all");
  const [cities, setCities] = useState([]);

  useEffect(() => {
    if (!user?.id) return; // Wait for user to be loaded
    
    if (!isCityAdmin) {
      fetchCities();
    }
    fetchData();
  }, [user?.id, heatmapType, timeRange, selectedCity]);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities?admin_id=${user.id}`);
      // Sort cities alphabetically
      const sortedCities = (res.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
    } catch (err) {
      console.error("Failed to fetch cities:", err);
    }
  };

  const fetchData = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        days: timeRange,
        type: heatmapType
      });
      
      if (isCityAdmin && user?.assigned_city_id) {
        params.append("city_id", user.assigned_city_id);
      } else if (selectedCity && selectedCity !== "all") {
        params.append("city_id", selectedCity);
      } else {
        params.append("admin_id", user.id);
      }

      console.log("[Heatmap] Fetching data with params:", params.toString());

      const [heatmapRes, statsRes] = await Promise.all([
        axios.get(`${API}/heatmap/rides?${params.toString()}`),
        axios.get(`${API}/heatmap/stats?${params.toString().replace(`type=${heatmapType}`, '')}`)
      ]);

      console.log("[Heatmap] Response:", heatmapRes.data);
      setHeatmapData(heatmapRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Failed to fetch heatmap data:", err);
      setError(err.message || "Failed to load heatmap data");
    } finally {
      setLoading(false);
    }
  };

  const getHeatmapOptions = () => {
    switch (heatmapType) {
      case "demand":
        return {
          radius: 30,
          blur: 20,
          gradient: { 0.0: '#3b82f6', 0.25: '#22c55e', 0.5: '#eab308', 0.75: '#f97316', 1.0: '#ef4444' }
        };
      case "drivers":
        return {
          radius: 25,
          blur: 15,
          gradient: { 0.0: '#6366f1', 0.5: '#8b5cf6', 1.0: '#a855f7' }
        };
      case "completed":
        return {
          radius: 25,
          blur: 18,
          gradient: { 0.0: '#14b8a6', 0.5: '#10b981', 1.0: '#22c55e' }
        };
      default:
        return {};
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color }) => (
    <div className="bg-card rounded-xl border p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
          {subValue && <p className="text-xs text-muted-foreground">{subValue}</p>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ride-heatmap">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Flame className="w-6 h-6 text-orange-500" />
            Ride Demand Heatmap
          </h1>
          <p className="text-muted-foreground">
            Visualize ride demand patterns and driver distribution
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-card rounded-xl border p-4">
        <div className="space-y-1">
          <Label className="text-xs">View Type</Label>
          <Select value={heatmapType} onValueChange={setHeatmapType}>
            <SelectTrigger className="w-[180px]" data-testid="heatmap-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="demand">
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-red-500" />
                  Ride Demand
                </span>
              </SelectItem>
              <SelectItem value="drivers">
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-500" />
                  Driver Locations
                </span>
              </SelectItem>
              <SelectItem value="completed">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Completed Rides
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Time Range</Label>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]" data-testid="time-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!isCityAdmin && cities.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs">City</Label>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="w-[180px]" data-testid="city-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city.id} value={city.id}>
                    {city.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-end">
          <div className="text-sm text-muted-foreground">
            {heatmapData.total_points} data points
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Car}
            label="Total Rides"
            value={stats.total_rides}
            subValue={`Last ${timeRange} days`}
            color="bg-blue-500"
          />
          <StatCard
            icon={CheckCircle}
            label="Completed"
            value={stats.completed_rides}
            subValue={`${stats.completion_rate}% rate`}
            color="bg-green-500"
          />
          <StatCard
            icon={XCircle}
            label="Cancelled"
            value={stats.cancelled_rides}
            color="bg-red-500"
          />
          <StatCard
            icon={Users}
            label="Online Drivers"
            value={`${stats.online_drivers}/${stats.total_drivers}`}
            subValue={`${stats.driver_utilization}% active`}
            color="bg-purple-500"
          />
        </div>
      )}

      {/* Peak Hours */}
      {stats?.peak_hours?.length > 0 && (
        <div className="bg-card rounded-xl border p-4">
          <h3 className="font-semibold flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4" />
            Peak Demand Hours
          </h3>
          <div className="flex flex-wrap gap-3">
            {stats.peak_hours.map((peak, idx) => (
              <div
                key={idx}
                className={`px-4 py-2 rounded-full ${
                  idx === 0 ? 'bg-red-100 text-red-700 border border-red-200' :
                  idx === 1 ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                  'bg-yellow-100 text-yellow-700 border border-yellow-200'
                }`}
              >
                <span className="font-semibold">
                  {peak.hour.toString().padStart(2, '0')}:00
                </span>
                <span className="ml-2 text-sm opacity-80">
                  ({peak.rides} rides)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Heatmap */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-500" />
            {heatmapType === "demand" && "Ride Pickup Locations"}
            {heatmapType === "drivers" && "Driver Positions"}
            {heatmapType === "completed" && "Ride Dropoff Locations"}
          </h3>
          <p className="text-sm text-muted-foreground">
            {heatmapType === "demand" && "Hotter areas indicate higher ride demand"}
            {heatmapType === "drivers" && "Shows current driver distribution"}
            {heatmapType === "completed" && "Popular destination areas"}
          </p>
        </div>
        
        <div className="h-[500px]">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center flex-col gap-4">
              <XCircle className="w-12 h-12 text-destructive" />
              <p className="text-muted-foreground">{error}</p>
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : (
            <MapContainer
              center={[heatmapData.center?.lat || 20.5937, heatmapData.center?.lng || 78.9629]}
              zoom={10}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapCenterUpdater center={heatmapData.center} zoom={heatmapData.points.length > 0 ? 11 : 5} />
              <HeatLayer points={heatmapData.points} options={getHeatmapOptions()} />
            </MapContainer>
          )}
        </div>

        {/* Legend */}
        <div className="p-4 border-t bg-muted/30">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Intensity:</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Low</span>
              <div 
                className="w-32 h-3 rounded-full"
                style={{
                  background: heatmapType === "demand" 
                    ? 'linear-gradient(to right, #3b82f6, #22c55e, #eab308, #f97316, #ef4444)'
                    : heatmapType === "drivers"
                    ? 'linear-gradient(to right, #6366f1, #8b5cf6, #a855f7)'
                    : 'linear-gradient(to right, #14b8a6, #10b981, #22c55e)'
                }}
              />
              <span className="text-xs text-muted-foreground">High</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RideHeatMap;
